// What a view's `update` hands back: the next model and effects, as data.
//
// Every view of the app answers commands the same way:
//
//   update(model, command) -> [model, effects]
//
// `update` is pure and total: for every command it either says what is to
// be done (effects the view's interpreter carries out: scroll there, focus
// that, write these marks), says the command has nothing to act on here
// and why (`not-applicable`, which the person is shown), or hands it to
// the scope around the view (`pass`). Nothing is ignored in silence.

import type { Command } from './command';
import { phrase } from './words';

/** The effects every view may return, with one meaning everywhere. */
export type Shared =
  /**
   * The command has nothing to act on here: `why` is said to the person,
   * small and out of the way. `instead` is a command that does what they
   * may have meant; the keys that reach it are said with it.
   */
  | { kind: 'not-applicable'; why: string; instead?: Command }
  /** Not this view's to answer: the scope around it is asked. */
  | { kind: 'pass' }
  /** The key is left to the browser or the focused control, as if unbound. */
  | { kind: 'leave' }
  /** Said to a screen reader (a polite live region). */
  | { kind: 'announce'; text: string };

/** A model and the effects to carry out. */
export type Update<M, E> = readonly [M, readonly (E | Shared)[]];

export const pass: Shared = { kind: 'pass' };
export const leave: Shared = { kind: 'leave' };
export const notApplicable = (why: string, instead?: Command): Shared => (instead ? { kind: 'not-applicable', why, instead } : { kind: 'not-applicable', why });
export const announce = (text: string): Shared => ({ kind: 'announce', text });

/** `command` has nothing to act on `where` ("in Drafts", "on the dashboard"). */
export const nothingFor = (command: Command, where: string, instead?: Command): Shared => notApplicable(`${phrase(command)}: nothing to do that to ${where}`, instead);

/** How a scope answered a command (keys/router.ts): carried out, handed outward, or left to the browser. */
export type Outcome = 'handled' | 'pass' | 'declined';

/** How the scope that returned `effects` answered. */
export function outcomeOf(effects: readonly { kind: string }[]): Outcome {
  if (effects.some((e) => e.kind === 'pass')) return 'pass';
  if (effects.some((e) => e.kind === 'leave')) return 'declined';
  return 'handled';
}
